import { Collapse } from '@chakra-ui/react';
import styled from '@emotion/styled';

import type {
  NavigationGutterItem,
  NavigationGutterItemChild,
} from '~/@types/redux';
import Badge from '~/common/components/Badge';
import NavAlertItemsContainer from '~/common/components/Nav/LeftDrawer/components/NavAlertItems/NavAlertItemsContainer';
import useLeftDrawerContext from '~/common/components/Nav/LeftDrawer/context/useLeftDrawerContext';
import { RULE_RESULT_STATUS_ID } from '~/common/constants';
import { useUser } from '~/common/hooks/useAuthentication';
import { useNavFeatureFlags } from '~/common/hooks/useNavFeatureFlags';
import {
  CLASSIFICATION,
  ClassifyRuleIds,
} from '~/pages/ClassifyDocumentsV2/constants';
import { useQuickMatchDocumentsQuery } from '~/pages/ClassifyDocumentsV2/hooks/queries';
import {
  StyledAccordionItem,
  StyledAccordionPanel,
} from '~/pages/Home/AlertTabAccordion';
import ChildAlert from '~/pages/Home/ChildAlert';
import ParentAlert from '~/pages/Home/ParentAlert';
import { SEARCH_MIN_CHARACTERS } from '~/pages/Home/constants';
import { useNavigation, useSelectedAlert } from '~/stores/navigationStore';
import { useNavSearchStore } from '~/stores/useNavSearchStore';
import type { GutterItem } from '~/types';

import Text from '../../common/components/Texts/Text';

const StyledCategoryGroupButton = styled('header')`
  display: flex;
  align-items: center;
  height: 32px;
  background-color: ${(props) => props.theme.colors?.white};
  border-bottom: 1px solid ${(props) => props.theme.colors?.gray[200]};
  padding: 0.5rem 0.25rem 0.5rem 1.25rem;
  pointer-events: none;
`;

function filterDocument(
  searchQuery: string | null,
  document: NavigationGutterItemChild,
  selectedDocument: GutterItem,
) {
  const filter =
    searchQuery.trim().length > SEARCH_MIN_CHARACTERS
      ? searchQuery.trim().toLowerCase()
      : null;

  if (!filter || document.gutterItemId === selectedDocument.gutterItemId) {
    return true;
  }

  if (!filter) return true;
  return !!(
    document.displayTitle.toLowerCase().includes(filter) ||
    document.additionalReferences.DocumentCategory?.toLowerCase().includes(
      filter,
    )
  );
}

function QuickMatchAlert({ alert }: Readonly<{ alert: NavigationGutterItem }>) {
  const { data: quickMatchData, isLoading } = useQuickMatchDocumentsQuery();
  const { selectedCategory } = useNavigation();
  const { completed, needed } = (
    quickMatchData?.screenData?.quickMatchCategory ?? []
  ).reduce(
    (accum, { categoryStatus, tableData }) => {
      const category =
        categoryStatus === CLASSIFICATION.completed ? 'completed' : 'needed';

      accum[category] += tableData.length;

      return accum;
    },
    { completed: 0, needed: 0 },
  );

  const isIncomplete =
    (isLoading &&
      selectedCategory.navigationGutterItems.some(
        (x) => x.ruleId === ClassifyRuleIds.DocumentAction,
      )) ||
    needed > 0;

  return (
    <ParentAlert
      alert={alert}
      badgeCount={needed || completed || ' '}
      status={
        isIncomplete
          ? RULE_RESULT_STATUS_ID.FIRED
          : RULE_RESULT_STATUS_ID.CLEARED
      }
    />
  );
}

function ClassifyAccordion({
  alert,
}: Readonly<{ alert: NavigationGutterItem }>) {
  const { showParentChildren } = useLeftDrawerContext();
  const selectedAlert = useSelectedAlert();
  const showChildAlert = showParentChildren[alert?.gutterItemId];
  const searchQuery = useNavSearchStore((store) => store.searchQuery);

  const groupedAlerts = Object.entries(
    Object.groupBy(
      alert.gutterItemChildren,
      (child) => child.additionalReferences.DocumentCategory,
    ),
  );

  if (alert.ruleId === ClassifyRuleIds.QuickMatch) {
    return <QuickMatchAlert alert={alert} />;
  }

  return (
    <StyledAccordionItem key={alert.ruleId}>
      <div role="tablist">
        <ParentAlert
          alert={alert}
          badgeCount={alert.gutterItemChildren.length}
        />
      </div>
      <StyledAccordionPanel role="tablist">
        <Collapse in={showChildAlert}>
          {groupedAlerts.map(([groupHeader, groupAlerts]) => (
            <section key={groupHeader}>
              <StyledCategoryGroupButton>
                <Badge
                  content={groupAlerts.length}
                  color={
                    groupAlerts[0].ruleResultStatusId ===
                    RULE_RESULT_STATUS_ID.CLEARED
                      ? 'green'
                      : 'red'
                  }
                />
                <Text
                  as="h4"
                  sx={{
                    ml: 2,
                    textAlign: 'left',
                    lineHeight: '1rem',
                    fontSize: '12px',
                    letterSpacing: '0px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {groupHeader}
                </Text>
              </StyledCategoryGroupButton>
              {groupAlerts
                .filter((childAlert) =>
                  filterDocument(searchQuery, childAlert, selectedAlert),
                )
                .map((childAlert) => (
                  <ChildAlert
                    parentAlert={alert}
                    alert={childAlert}
                    key={childAlert?.parentRuleSortOrder}
                    circleIcon
                  />
                ))}
            </section>
          ))}
        </Collapse>
      </StyledAccordionPanel>
    </StyledAccordionItem>
  );
}

export default function ClassifyNav() {
  const { selectedCategory } = useNavigation();
  const { IsClassifyDocumentSearchEnabled } = useNavFeatureFlags();
  const { isUnderwriter, hasAccessToUnderwriterView } = useUser();

  if (
    !selectedCategory ||
    selectedCategory.navigationGutterItems.some(
      (x) => x.ruleId === ClassifyRuleIds.Error,
    )
  ) {
    return null;
  }

  return (
    <NavAlertItemsContainer
      ariaLabel="classify navigation"
      enableSearch={
        IsClassifyDocumentSearchEnabled &&
        (isUnderwriter || hasAccessToUnderwriterView)
      }
    >
      {selectedCategory.navigationGutterItems.map((alert) => (
        <ClassifyAccordion alert={alert} key={alert.ruleId} />
      ))}
    </NavAlertItemsContainer>
  );
}
